import { z } from "zod";
import {
  API_KEY_MODES,
  API_KEY_SCOPES,
  APP_STATUSES,
  CHANNELS,
  CREDENTIAL_STATUSES,
  LIMITS,
  PROVIDER_STATUSES,
  USER_ROLES,
} from "../constants";
import { IsoDate, paginated, Uuid } from "./common";
import { CredentialAlias } from "./messages";

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const LoginRequest = z
  .strictObject({
    email: z.email(),
    password: z.string().min(1).max(256),
  })
  .meta({ id: "LoginRequest" });
export type LoginRequest = z.infer<typeof LoginRequest>;

export const ChangePasswordRequest = z
  .strictObject({
    current_password: z.string().min(1).max(256),
    new_password: z
      .string()
      .min(LIMITS.passwordMinLength, `Password must be at least ${LIMITS.passwordMinLength} characters`)
      .max(256),
  })
  .meta({ id: "ChangePasswordRequest" });
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

export const UpdateProfileRequest = z
  .strictObject({ name: z.string().trim().min(1).max(120) })
  .meta({ id: "UpdateProfileRequest" });
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequest>;

export const Me = z
  .object({
    id: Uuid,
    email: z.string(),
    name: z.string(),
    role: z.enum(USER_ROLES),
    is_superadmin: z.boolean(),
    org: z.object({ id: Uuid, name: z.string() }),
  })
  .meta({ id: "Me" });
export type Me = z.infer<typeof Me>;

// ---------------------------------------------------------------------------
// Apps
// ---------------------------------------------------------------------------

export const Slug = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug may only contain lowercase letters, digits and single dashes");

/** Short reference to a credential profile (D-44). */
export const CredentialProfileRef = z
  .object({ id: Uuid, name: z.string() })
  .meta({ id: "CredentialProfileRef" });
export type CredentialProfileRef = z.infer<typeof CredentialProfileRef>;

export const App = z
  .object({
    id: Uuid,
    name: z.string(),
    slug: z.string(),
    status: z.enum(APP_STATUSES),
    settings: z.record(z.string(), z.unknown()),
    credential_profile: CredentialProfileRef.meta({
      description: "The credentials this app sends with. Switch it to move every alias at once (D-44).",
    }),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "App" });
export type App = z.infer<typeof App>;

export const AppList = z.array(App).meta({ id: "AppList" });
export type AppList = z.infer<typeof AppList>;

export const CreateAppRequest = z
  .strictObject({
    name: z.string().min(1).max(120),
    slug: Slug.optional().meta({ description: "Generated from the name when empty." }),
    credential_profile_id: Uuid.optional().meta({
      description:
        "Use an existing credential profile. When empty, a new profile named after the app is created.",
    }),
  })
  .meta({ id: "CreateAppRequest" });
export type CreateAppRequest = z.infer<typeof CreateAppRequest>;

export const UpdateAppRequest = z
  .strictObject({
    name: z.string().min(1).max(120).optional(),
    status: z.enum(APP_STATUSES).optional(),
    credential_profile_id: Uuid.optional().meta({
      description: "Switch the app to another credential profile. Takes effect within a few seconds.",
    }),
  })
  .meta({ id: "UpdateAppRequest" });
export type UpdateAppRequest = z.infer<typeof UpdateAppRequest>;

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------

export const ApiKey = z
  .object({
    id: Uuid,
    name: z.string(),
    prefix: z
      .string()
      .meta({ description: "Start of the key, for identification. The full key is shown only once." }),
    mode: z.enum(API_KEY_MODES),
    scopes: z.array(z.enum(API_KEY_SCOPES)),
    last_used_at: IsoDate.nullable(),
    revoked_at: IsoDate.nullable(),
    created_at: IsoDate,
  })
  .meta({ id: "ApiKey" });
export type ApiKey = z.infer<typeof ApiKey>;

export const ApiKeyList = z.array(ApiKey).meta({ id: "ApiKeyList" });
export type ApiKeyList = z.infer<typeof ApiKeyList>;

export const CreateApiKeyRequest = z
  .strictObject({
    name: z.string().min(1).max(120),
    mode: z.enum(API_KEY_MODES).default("live"),
    scopes: z
      .array(z.enum(API_KEY_SCOPES))
      .min(1)
      .optional()
      .meta({ description: "Defaults to all scopes." }),
  })
  .meta({ id: "CreateApiKeyRequest" });
export type CreateApiKeyRequest = z.infer<typeof CreateApiKeyRequest>;
export type CreateApiKeyInput = z.input<typeof CreateApiKeyRequest>;

export const CreatedApiKey = ApiKey.extend({
  key: z.string().meta({ description: "The full key. Store it now, it cannot be shown again." }),
}).meta({ id: "CreatedApiKey" });
export type CreatedApiKey = z.infer<typeof CreatedApiKey>;

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

export const Provider = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    channel: z.enum(CHANNELS),
    adapter_key: z.string(),
    status: z.enum(PROVIDER_STATUSES),
    status_note: z.string().nullable(),
    config_schema: z.record(z.string(), z.unknown()).meta({
      description:
        "JSON Schema of the credential config. Fields with `x-secret: true` are stored encrypted. `x-order` lists the field order.",
    }),
    capabilities: z.record(z.string(), z.unknown()),
    updated_at: IsoDate,
  })
  .meta({ id: "Provider" });
export type Provider = z.infer<typeof Provider>;

export const ProviderList = z.array(Provider).meta({ id: "ProviderList" });
export type ProviderList = z.infer<typeof ProviderList>;

export const UpdateProviderRequest = z
  .strictObject({
    status: z.enum(PROVIDER_STATUSES),
    status_note: z.string().max(500).nullable().optional(),
  })
  .meta({ id: "UpdateProviderRequest" });
export type UpdateProviderRequest = z.infer<typeof UpdateProviderRequest>;

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

export const ProviderRef = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  channel: z.enum(CHANNELS),
  status: z.enum(PROVIDER_STATUSES),
});

export const CredentialHealth = z
  .object({
    consecutive_failures: z.int().optional(),
    degraded_until: IsoDate.nullable().optional(),
    last_success_at: IsoDate.nullable().optional(),
    last_error: z.string().nullable().optional(),
    last_checked_at: IsoDate.nullable().optional(),
  })
  .meta({ id: "CredentialHealth" });

export const Credential = z
  .object({
    id: Uuid,
    name: z.string(),
    provider: ProviderRef,
    status: z.enum(CREDENTIAL_STATUSES),
    config_public: z
      .record(z.string(), z.unknown())
      .meta({ description: "The non-secret part of the config." }),
    secret_fields_set: z.array(z.string()).meta({ description: "Secret fields that have a value." }),
    health: CredentialHealth,
    used_by: z.object({ profiles: z.int(), apps: z.int() }).meta({
      description: "Credential profiles that contain this credential, and apps that use those profiles.",
    }),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "Credential" });
export type Credential = z.infer<typeof Credential>;

export const CredentialList = z.array(Credential).meta({ id: "CredentialList" });
export type CredentialList = z.infer<typeof CredentialList>;

export const CreateCredentialRequest = z
  .strictObject({
    provider_id: Uuid,
    name: z.string().min(1).max(120),
    config: z
      .record(z.string(), z.unknown())
      .meta({ description: "Validated against the provider `config_schema`." }),
  })
  .meta({ id: "CreateCredentialRequest" });
export type CreateCredentialRequest = z.infer<typeof CreateCredentialRequest>;

export const UpdateCredentialRequest = z
  .strictObject({
    name: z.string().min(1).max(120).optional(),
    status: z.enum(CREDENTIAL_STATUSES).optional(),
    config: z
      .record(z.string(), z.unknown())
      .optional()
      .meta({ description: "Secret fields that are empty or omitted keep their current value." }),
  })
  .meta({ id: "UpdateCredentialRequest" });
export type UpdateCredentialRequest = z.infer<typeof UpdateCredentialRequest>;

export const CredentialTestResult = z
  .object({
    ok: z.boolean(),
    error: z.string().nullable(),
    duration_ms: z.int(),
  })
  .meta({ id: "CredentialTestResult" });
export type CredentialTestResult = z.infer<typeof CredentialTestResult>;

// ---------------------------------------------------------------------------
// Credential profiles (D-44)
// ---------------------------------------------------------------------------

/** One credential in a profile, under an alias. */
export const ProfileCredential = z
  .object({
    id: Uuid,
    profile_id: Uuid,
    alias: z.string(),
    priority: z.int(),
    is_active: z.boolean(),
    channel: z.enum(CHANNELS),
    credential: z.object({
      id: Uuid,
      name: z.string(),
      status: z.enum(CREDENTIAL_STATUSES),
      provider: ProviderRef,
    }),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "ProfileCredential" });
export type ProfileCredential = z.infer<typeof ProfileCredential>;

export const CreateProfileCredentialRequest = z
  .strictObject({
    credential_id: Uuid,
    alias: CredentialAlias,
    priority: z.int().min(1).max(100).default(1),
    is_active: z.boolean().default(true),
  })
  .meta({ id: "CreateProfileCredentialRequest" });
export type CreateProfileCredentialRequest = z.infer<typeof CreateProfileCredentialRequest>;
export type CreateProfileCredentialInput = z.input<typeof CreateProfileCredentialRequest>;

export const UpdateProfileCredentialRequest = z
  .strictObject({
    credential_id: Uuid.optional().meta({
      description: "Swap the credential behind the alias. The channel must match (D-05).",
    }),
    alias: CredentialAlias.optional(),
    priority: z.int().min(1).max(100).optional(),
    is_active: z.boolean().optional(),
  })
  .meta({ id: "UpdateProfileCredentialRequest" });
export type UpdateProfileCredentialRequest = z.infer<typeof UpdateProfileCredentialRequest>;

export const CredentialProfile = z
  .object({
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    channels: z
      .array(z.object({ channel: z.enum(CHANNELS), aliases: z.array(z.string()) }))
      .meta({ description: "Aliases per channel, to compare profiles at a glance." }),
    credentials: z.int().meta({ description: "Number of credentials in the profile." }),
    apps: z
      .array(z.object({ id: Uuid, name: z.string() }))
      .meta({ description: "Apps that use this profile." }),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "CredentialProfile" });
export type CredentialProfile = z.infer<typeof CredentialProfile>;

export const CredentialProfileList = z.array(CredentialProfile).meta({ id: "CredentialProfileList" });
export type CredentialProfileList = z.infer<typeof CredentialProfileList>;

export const CredentialProfileDetail = CredentialProfile.extend({
  entries: z.array(ProfileCredential),
}).meta({ id: "CredentialProfileDetail" });
export type CredentialProfileDetail = z.infer<typeof CredentialProfileDetail>;

export const CreateCredentialProfileRequest = z
  .strictObject({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    copy_from: Uuid.optional().meta({
      description: "Start with the credentials of another profile, e.g. copy Production, then swap in mocks.",
    }),
  })
  .meta({ id: "CreateCredentialProfileRequest" });
export type CreateCredentialProfileRequest = z.infer<typeof CreateCredentialProfileRequest>;

export const UpdateCredentialProfileRequest = z
  .strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
  })
  .meta({ id: "UpdateCredentialProfileRequest" });
export type UpdateCredentialProfileRequest = z.infer<typeof UpdateCredentialProfileRequest>;

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export const AuditLog = z
  .object({
    id: Uuid,
    actor_type: z.enum(["user", "api_key", "system"]),
    actor_id: z.string().nullable(),
    actor_label: z.string().nullable(),
    action: z.string(),
    entity_type: z.string(),
    entity_id: z.string().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    ip: z.string().nullable(),
    created_at: IsoDate,
  })
  .meta({ id: "AuditLog" });
export type AuditLog = z.infer<typeof AuditLog>;

export const AuditLogList = paginated(AuditLog, "AuditLogList");
export type AuditLogList = z.infer<typeof AuditLogList>;

/** `data` of responses that have nothing to return, e.g. deletes. */
export const Empty = z.null().meta({ id: "Empty" });
